import React from 'react';
import {
  ArrowTopRightOnSquareIcon,
  PhoneIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import type { ConferenceData, ConferenceEntryPoint } from '../../../types/integrations';

interface EventMeetCardProps {
  data: ConferenceData;
}

const entryIcon = (type: ConferenceEntryPoint['type']) => {
  if (type === 'phone') return PhoneIcon;
  return VideoCameraIcon;
};

const EventMeetCard: React.FC<EventMeetCardProps> = ({ data }) => {
  if (!data.entry_points || data.entry_points.length === 0) return null;

  const video = data.entry_points.find((e) => e.type === 'video') ?? data.entry_points[0];

  return (
    <div className="rounded-2 border border-border-primary/15 bg-surface-elevated overflow-hidden">
      <div className="flex items-center gap-3 p-3">
        {data.solution_icon_uri ? (
          <img
            src={data.solution_icon_uri}
            alt=""
            className="w-8 h-8 rounded shrink-0"
          />
        ) : (
          <div className="w-8 h-8 rounded bg-surface-primary flex items-center justify-center shrink-0">
            <VideoCameraIcon className="w-4 h-4 text-text-secondary" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate">
            {data.solution_name || 'Video meeting'}
          </p>
          {video.uri && (
            <p className="text-xs text-text-tertiary truncate">{video.uri}</p>
          )}
        </div>
        {video.uri && (
          <a
            href={video.uri}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary btn-sm shrink-0 active:scale-[0.97] transition-transform"
          >
            Join
          </a>
        )}
      </div>
      {data.entry_points.length > 1 && (
        <ul className="border-t border-border-primary/15 divide-y divide-border-primary/10">
          {data.entry_points
            .filter((e) => e !== video)
            .map((entry, idx) => {
              const Icon = entryIcon(entry.type);
              return (
                <li
                  key={`${entry.type}-${idx}`}
                  className="flex items-center gap-3 px-3 py-2 text-xs"
                >
                  <Icon className="w-4 h-4 text-text-tertiary shrink-0" />
                  <span className="flex-1 min-w-0 truncate">
                    {entry.label || entry.uri}
                  </span>
                  {entry.pin && (
                    <span className="text-text-tertiary">PIN {entry.pin}</span>
                  )}
                  {entry.uri && (
                    <a
                      href={entry.uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-500 hover:underline inline-flex items-center gap-0.5"
                    >
                      <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                      Open
                    </a>
                  )}
                </li>
              );
            })}
        </ul>
      )}
      {data.notes && (
        <div className="px-3 py-2 border-t border-border-primary/15 text-xs text-text-tertiary whitespace-pre-line">
          {data.notes}
        </div>
      )}
    </div>
  );
};

export default EventMeetCard;
